import { createTheme } from '@mui/material/styles';

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1440,
    xxl: 1700,
  },
};

const { up } = createTheme({ breakpoints }).breakpoints;

const getColor = (r, g, b) => ({
  main: `rgba(${r},${g},${b}, 1)`,
  darkest: `rgba(${r},${g},${b}, 0.9)`,
  darker: `rgba(${r},${g},${b}, 0.8)`,
  dark: `rgba(${r},${g},${b}, 0.7)`,
  semiDark: `rgba(${r},${g},${b}, 0.6)`,
  medium: `rgba(${r},${g},${b}, 0.5)`,
  semiLight: `rgba(${r},${g},${b}, 0.4)`,
  light: `rgba(${r},${g},${b}, 0.3)`,
  lighter: `rgba(${r},${g},${b}, 0.2)`,
  lightest: `rgba(${r},${g},${b}, 0.1)`,
});

const lightPalette = {
  type: 'light',
  primary: { ...getColor(159, 119, 235) },
  secondaryBlack: { ...getColor(0, 0, 0) },
  secondaryWhite: { ...getColor(255, 255, 255) },
  colorRed: { ...getColor(255, 95, 99) },
  colorGreen: { ...getColor(0, 191, 111) },
  colorBlue: { ...getColor(119, 138, 235) },
  dynamicBlack: { ...getColor(30, 30, 30) },
  shoeWax: { ...getColor(43, 43, 43) },
  carbonFiber: { ...getColor(45, 45, 45) },
  lunarBase: { ...getColor(134, 134, 134) },
  lightSilver: { ...getColor(217, 217, 217) },
  lynxWhite: { ...getColor(247, 247, 247) },
  darkPurple: { ...getColor(43, 30, 89) },
  japanBlush: { ...getColor(222, 214, 241) },
  paperWhite: { ...getColor(238, 237, 242) },
  fusionRed: { ...getColor(255, 95, 99) },
  salsifyGrass: { ...getColor(43, 177, 115) },
  winterMist: { ...getColor(229, 255, 234) },
  redContrast: { ...getColor(180, 49, 49) },
  pinkTheory: { ...getColor(255, 229, 229) },
  venetianRed: { ...getColor(225, 159, 49) },
  robinEggBlue: { ...getColor(0, 191, 111) },
  purpleSand: { ...getColor(197, 173, 243) },
  lavender: { ...getColor(211, 151, 248) },
  sunShade: { ...getColor(255, 159, 49) },
  orange: { ...getColor(255, 165, 0) },
  pastelLavender: { ...getColor(222, 214, 241) },
  forestGreen: { ...getColor(34, 139, 34) },
  deepMidnightBlue: { ...getColor(43, 30, 89) },
  royalBlue: { ...getColor(53, 122, 232) },
  darkGrey: { ...getColor(34, 34, 34) },
  lavenderMist: { ...getColor(222, 205, 255) },
  jadeGreen: { ...getColor(43, 177, 115) },
  freshGreen: { ...getColor(108, 158, 46) },
  deepAmethyst: { ...getColor(46, 37, 89) },
  greenFleck: { ...getColor(102, 154, 38) },
  fieryRose: { ...getColor(237, 85, 86) },
  lightGrey: { ...getColor(170, 165, 189) },
};

const darkPalette = {
  type: 'dark',
  primary: { ...getColor(159, 119, 235) },
  secondaryBlack: { ...getColor(0, 0, 0) },
  secondaryWhite: { ...getColor(255, 255, 255) },
  colorRed: { ...getColor(255, 95, 99) },
  colorGreen: { ...getColor(0, 191, 111) },
  colorBlue: { ...getColor(119, 138, 235) },
  dynamicBlack: { ...getColor(30, 30, 30) },
  shoeWax: { ...getColor(43, 43, 43) },
  carbonFiber: { ...getColor(45, 45, 45) },
  lunarBase: { ...getColor(134, 134, 134) },
  lightSilver: { ...getColor(217, 217, 217) },
  lynxWhite: { ...getColor(247, 247, 247) },
  darkPurple: { ...getColor(43, 30, 89) },
  japanBlush: { ...getColor(222, 214, 241) },
  paperWhite: { ...getColor(238, 237, 242) },
  fusionRed: { ...getColor(255, 95, 99) },
  salsifyGrass: { ...getColor(43, 177, 115) },
  winterMist: { ...getColor(229, 255, 234) },
  redContrast: { ...getColor(180, 49, 49) },
  pinkTheory: { ...getColor(255, 229, 229) },
  venetianRed: { ...getColor(225, 159, 49) },
  robinEggBlue: { ...getColor(0, 191, 111) },
  purpleSand: { ...getColor(197, 173, 243) },
  lavender: { ...getColor(211, 151, 248) },
  orange: { ...getColor(255, 165, 0) },
  pastelLavender: { ...getColor(222, 214, 241) },
  forestGreen: { ...getColor(34, 139, 34) },
  deepMidnightBlue: { ...getColor(43, 30, 89) },
  royalBlue: { ...getColor(53, 122, 232) },
  darkGrey: { ...getColor(34, 34, 34) },
  lavenderMist: { ...getColor(222, 205, 255) },
  jadeGreen: { ...getColor(43, 177, 115) },
  freshGreen: { ...getColor(108, 158, 46) },
  deepAmethyst: { ...getColor(46, 37, 89) },
  greenFleck: { ...getColor(102, 154, 38) },
  fieryRose: { ...getColor(237, 85, 86) },
};

const theme = createTheme({
  breakpoints,
  palette: lightPalette,
  typography: {
    h2: {
      fontFamily: 'Poppins',
      fontWeight: '600',
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '12px',
      },
      [up('sm')]: {
        fontSize: '30px',
      },
      [up('md')]: {
        fontSize: '36px',
      },
      [up('lg')]: {
        fontSize: '48px',
      },
      [up('xl')]: {
        fontSize: '56px',
      },
      [up('xxl')]: {
        fontSize: '64px',
      },
    },
    h4: {
      fontFamily: 'Poppins',
      fontWeight: '600',
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '7px',
      },
      [up('sm')]: {
        fontSize: '10px',
      },
      [up('md')]: {
        fontSize: '13px',
      },
      [up('lg')]: {
        fontSize: '17px',
      },
      [up('xl')]: {
        fontSize: '21px',
      },
      [up('xxl')]: {
        fontSize: '24px',
      },
    },
    h5: {
      fontFamily: 'Heebo',
      fontWeight: 500,
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '10px',
      },
      [up('sm')]: {
        fontSize: '11px',
      },
      [up('md')]: {
        fontSize: '12px',
      },
      [up('lg')]: {
        fontSize: '14px',
      },
      [up('xl')]: {
        fontSize: '16px',
      },
      [up('xxl')]: {
        fontSize: '18px',
      },
    },
    h6: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '13px',
      },
      [up('sm')]: {
        fontSize: '15px',
      },
      [up('md')]: {
        fontSize: '16px',
      },
      [up('lg')]: {
        fontSize: '17px',
      },
      [up('xl')]: {
        fontSize: '18px',
      },
      [up('xxl')]: {
        fontSize: '20px',
      },
    },
    subtitle1: {
      fontWeight: 400,
      fontFamily: 'Heebo',
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '7px',
      },
      [up('sm')]: {
        fontSize: '7px',
      },
      [up('md')]: {
        fontSize: '9px',
      },
      [up('lg')]: {
        fontSize: '11px',
      },
      [up('xl')]: {
        fontSize: '13px',
      },
      [up('xxl')]: {
        fontSize: '15px',
      },
    },
    subtitle2: {
      fontWeight: 400,
      fontFamily: 'Heebo',
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '8px',
      },
      [up('sm')]: {
        fontSize: '8px',
      },
      [up('md')]: {
        fontSize: '10px',
      },
      [up('lg')]: {
        fontSize: '12px',
      },
      [up('xl')]: {
        fontSize: '13px',
      },
      [up('xxl')]: {
        fontSize: '13px',
      },
    },
    body1: {
      fontFamily: 'Heebo',
      fontWeight: 500,
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '10px',
      },
      [up('sm')]: {
        fontSize: '11px',
      },
      [up('md')]: {
        fontSize: '11px',
      },
      [up('lg')]: {
        fontSize: '12px',
      },
      [up('xl')]: {
        fontSize: '14px',
      },
      [up('xxl')]: {
        fontSize: '16px',
      },
    },
    body2: {
      fontFamily: 'Heebo',
      fontWeight: 400,
      letterSpacing: 0,
      [up('xs')]: {
        fontSize: '10px',
      },
      [up('sm')]: {
        fontSize: '11px',
      },
      [up('md')]: {
        fontSize: '11px',
      },
      [up('lg')]: {
        fontSize: '12px',
      },
      [up('xl')]: {
        fontSize: '13px',
      },
      [up('xxl')]: {
        fontSize: '14px',
      },
    },
  },

  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontFamily: 'Heebo',
            fontWeight: 400,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: lightPalette.primary.main,
          },
          fontFamily: 'Heebo',
          fontWeight: 400,
          [up('xs')]: {
            fontSize: '10px',
          },
          [up('sm')]: {
            fontSize: '12px',
          },
          [up('md')]: {
            fontSize: '14px',
          },
          [up('lg')]: {
            fontSize: '15px',
          },
          [up('xl')]: {
            fontSize: '16px',
          },
          [up('xxl')]: {
            fontSize: '16px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              borderColor: lightPalette.primary.main,
            },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: 'Heebo',
          fontWeight: 400,
          // [up('xs')]: {
          //   height: "5px"
          // },
          // [up('sm')]: {
          //   height: "0px"
          // },
          // [up('md')]: {
          //   height: "50px"
          // },
          // [up('lg')]: {
          //   height: "30px"
          // },
          // [up('xl')]: {
          //   height: "60px"
          // },
          // [up('xxl')]: {
          //   height: "80px"
          // },
          // borderRadius: "5px",
          // border: "1px solid rgba(0, 0, 0, 0.20)",
          // [up("xs")]: {
          //   fontSize: "10px",
          // },
          // [up("sm")]: {
          //   fontSize: "12px",
          // },
          // [up("md")]: {
          //   fontSize: "14px",
          // },
          // [up("lg")]: {
          //   fontSize: "15px",
          // },
          // [up("xl")]: {
          //   fontSize: "16px",
          // },
          // [up("xxl")]: {
          //   fontSize: "16px",
          // },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Heebo',
          fontWeight: 500,
          borderRadius: '5px',
          textTransform: 'none',
          boxShadow: 'none',
          padding: 0,
          letterSpacing: 0,
          [up('xs')]: {
            fontSize: '10px',
          },
          [up('sm')]: {
            fontSize: '11px',
          },
          [up('md')]: {
            fontSize: '11px',
          },
          [up('lg')]: {
            fontSize: '12px',
          },
          [up('xl')]: {
            fontSize: '14px',
          },
          [up('xxl')]: {
            fontSize: '16px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
          fontFamily: 'Heebo',
          fontWeight: 400,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: `0px 2px 8px ${lightPalette.secondaryBlack.lighter}`,
          '&::-webkit-scrollbar': {
            width: '6px',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: `${lightPalette.lynxWhite.main}`,
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: `${lightPalette.secondaryBlack.lighter}`,
            borderRadius: '8px',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Heebo',
          fontWeight: 400,
          '&:hover': {
            backgroundColor: lightPalette.lunarBase.lightest,
            color: lightPalette.secondaryBlack.main,
          },
          '&.Mui-selected': {
            backgroundColor: lightPalette.primary.main,
            color: lightPalette.secondaryWhite.main,
          },
          '&.Mui-selected:hover': {
            backgroundColor: lightPalette.primary.main,
            color: lightPalette.secondaryWhite.main,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '0',
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          height: '250px',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '0.4em',
          },
          '&::-webkit-scrollbar-track': {
            background: `${lightPalette.lightSilver.main}`,
            borderRadius: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: `${lightPalette.lunarBase.main}`,
            borderRadius: '5px',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: '7px',
          background: `${lightPalette.lightSilver.main}`,
        },
        bar: {
          background: `${lightPalette.primary.main}`,
          borderRadius: '7px',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          // fill: 'none',
          [up('xs')]: {
            fontSize: '13px',
          },
          [up('sm')]: {
            fontSize: '14px',
          },
          [up('md')]: {
            fontSize: '15px',
          },
          [up('lg')]: {
            fontSize: '20px',
          },
          [up('xl')]: {
            fontSize: '21px',
          },
          [up('xxl')]: {
            fontSize: '25px',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: `${lightPalette.primary.main}`,
          },
        },
      },
    },
  },
});

export { theme, breakpoints, up, lightPalette, darkPalette };
